import { UPLOAD_DRAIN_POLL_INTERVAL_MS, type UploadDrainWakePolicy } from "@uploads";
import { EventBus } from "@/helpers/eventBus";

const WARMUP_DELAYS_MS = [2000, 6000, 15000, 30000, 60000, 90000];
const PENDING_BACKOFF_DELAYS_MS = [5000, 10000, 20000];

interface AdminWakeHost {
	$route?: { name?: unknown };
	_uploadResumeInterval: ReturnType<typeof setInterval> | null;
	_uploadResumeWarmupTimers: ReturnType<typeof setTimeout>[] | null;
	_uploadResumeRecoveryTimer: ReturnType<typeof setTimeout> | null;
	_uploadResumeRecoveryAttempt: number;
	_onlineResumeHandler: ((event?: Event) => void) | null;
	_uploadDeferredResumeHandler: (() => void) | null;
}

interface AdminUploadWakePolicy extends UploadDrainWakePolicy {
	scheduleRecovery(resetBackoff?: boolean): void;
	clearRecovery(): void;
}

export function createAdminUploadWakePolicy(
	host: AdminWakeHost,
	runStartupReclaim: () => Promise<void>,
	rearmSmallFileUploads: () => void = () => undefined
): AdminUploadWakePolicy {
	let wake: (reason: string) => void = () => undefined;
	let isDraining = () => false;
	let recoveryScheduledAt: number | null = null;
	const wakeUnlessDevtoolsUploads = (reason: string, rearmSmallFiles = false): void => {
		if (host.$route?.name === "devtools-uploads") return;
		if (rearmSmallFiles) rearmSmallFileUploads();
		wake(reason);
	};
	const clearRecovery = (idleObservedAt?: number) => {
		if (idleObservedAt !== undefined && recoveryScheduledAt !== null && recoveryScheduledAt > idleObservedAt) return;
		if (host._uploadResumeRecoveryTimer) globalThis.clearTimeout(host._uploadResumeRecoveryTimer);
		host._uploadResumeRecoveryTimer = null;
		host._uploadResumeRecoveryAttempt = 0;
		recoveryScheduledAt = null;
	};
	const scheduleRecovery = (resetBackoff = false) => {
		if (resetBackoff) host._uploadResumeRecoveryAttempt = 0;
		if (host._uploadResumeRecoveryTimer) return;
		const delay = PENDING_BACKOFF_DELAYS_MS[Math.min(host._uploadResumeRecoveryAttempt, 2)];
		host._uploadResumeRecoveryAttempt++;
		recoveryScheduledAt = Date.now();
		host._uploadResumeRecoveryTimer = globalThis.setTimeout(() => {
			host._uploadResumeRecoveryTimer = null;
			recoveryScheduledAt = null;
			if (isDraining() || (host as AdminWakeHost & { _isResumingUploads?: boolean })._isResumingUploads) return scheduleRecovery();
			wakeUnlessDevtoolsUploads("pending-backoff");
		}, delay);
	};
	return {
		start(wakeDrain, getIsDraining) {
			wake = wakeDrain;
			isDraining = getIsDraining;
			host._uploadResumeInterval = globalThis.setInterval(
				() => wakeUnlessDevtoolsUploads("poll", true),
				UPLOAD_DRAIN_POLL_INTERVAL_MS
			);
			host._onlineResumeHandler = (event?: Event) => {
				if (event?.type === "visibilitychange" && document.visibilityState !== "visible") return;
				wakeUnlessDevtoolsUploads(event?.type ?? "online", true);
			};
			window.addEventListener("online", host._onlineResumeHandler);
			document.addEventListener("visibilitychange", host._onlineResumeHandler);
			window.addEventListener("pageshow", host._onlineResumeHandler);
			host._uploadDeferredResumeHandler = () => scheduleRecovery(true);
			EventBus.$on("UploadDeferred", host._uploadDeferredResumeHandler);
			host._uploadResumeWarmupTimers = WARMUP_DELAYS_MS.map((delay) =>
				globalThis.setTimeout(async () => {
					await runStartupReclaim();
					wakeUnlessDevtoolsUploads("warmup");
				}, delay)
			);
		},
		stop() {
			if (host._uploadResumeInterval) globalThis.clearInterval(host._uploadResumeInterval);
			host._uploadResumeInterval = null;
			host._uploadResumeWarmupTimers?.forEach((timer) => globalThis.clearTimeout(timer));
			host._uploadResumeWarmupTimers = null;
			clearRecovery();
			if (host._onlineResumeHandler) {
				window.removeEventListener("online", host._onlineResumeHandler);
				document.removeEventListener("visibilitychange", host._onlineResumeHandler);
				window.removeEventListener("pageshow", host._onlineResumeHandler);
			}
			host._onlineResumeHandler = null;
			if (host._uploadDeferredResumeHandler) EventBus.$off("UploadDeferred", host._uploadDeferredResumeHandler);
			host._uploadDeferredResumeHandler = null;
		},
		pendingObserved: scheduleRecovery,
		idleObserved: (observedAt) => clearRecovery(observedAt),
		claimMissed: scheduleRecovery,
		scheduleRecovery,
		clearRecovery
	};
}
